import { TextButton } from 'components/common';
import { FilterSelectsGroup } from 'components/core';
import { useContext, useState } from 'react';
import { FilterContext } from 'utils/context';
import { TFilter, TFilterOptions } from 'utils/context/FilterContext/types';

import { Box, Flex, Icon, Typography } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

interface IFilterSelectsGroupSectionProps {
  buttonText: string;
  icons: { [key: string]: () => JSX.Element };
  options: Array<{
    config: TFilterOptions;
    startVariant?: string;
    value: string;
  }>;
  placeholder: string;
  title: string;
}

function FilterSelectsGroupSection({
  buttonText,
  icons,
  options,
  placeholder,
  title,
}: IFilterSelectsGroupSectionProps) {
  const { t } = useTranslation();
  const { cache, filters, setFiltersKey, setFiltersKeys } =
    useContext(FilterContext);
  const [showUndo, setShowUndo] = useState(false);
  const filter = (filters[title] as string[]) || [];

  const handleAddSelect = () => {
    setFiltersKey(title, filter.concat(['']));
    setShowUndo(false);
  };

  const handleClear = (index: number) => {
    setFiltersKeys({
      [title]: filter.filter((_, idx) => idx !== index),
      [filters[title][index] as string]: '',
    });
    setShowUndo(true);
  };

  const handleSelect = (value: string, index: number) => {
    const selects = filter.concat([]);
    selects[index] = value;
    setFiltersKeys({
      [title]: selects,
      [filter[index]]: '',
    });
    setShowUndo(false);
  };

  const handleUndo = () => {
    const last = { [title]: cache[title] || [] } as TFilter;
    (last[title] as string[]).forEach((bar) => {
      last[bar] = cache[bar];
    });
    setFiltersKeys(last);
    setShowUndo(false);
  };

  return (
    <Box>
      <Flex orientation="vertical" gap={filter.length ? 'medium' : undefined}>
        {filter.map((selection, index) => (
          <FilterSelectsGroup
            key={selection}
            handleSelect={(value: string) => {
              handleSelect(value, index);
            }}
            icons={icons}
            onClear={() => {
              handleClear(index);
            }}
            options={options.filter(
              ({ value }) => !filter.includes(value) || selection === value,
            )}
            placeholder={t(placeholder)}
            value={filter[index]}
          />
        ))}
      </Flex>
      <Flex
        align="center"
        gap="x-small"
        css={{ ...(filter.length && { marginTop: '$medium' }) }}
      >
        {filter.length !== options.length && (
          <TextButton
            isDisabled={filter.length !== filter.filter(Boolean).length}
            onPress={handleAddSelect}
            startIcon={<Icon icon="add" />}
            text={t(buttonText)}
          />
        )}
        {showUndo && (
          <>
            <Typography css={{ color: '#324DA9' }}>{'|'}</Typography>
            <TextButton onPress={handleUndo} text={t('global.undo')} />
          </>
        )}
      </Flex>
    </Box>
  );
}

export default FilterSelectsGroupSection;
